import { useState, useEffect, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { formatPhone, normalizePhoneDigits } from "@/lib/validators";
import { fetchAddress, formatCEP } from "@/lib/cep";
import { useIBGEStates } from "@/hooks/useIBGELocations";
import { User, Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import { DocumentInput } from "@/components/DocumentInput";
import { validateDocument } from "@/utils/document";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface BuyerData {
  fullName: string;
  email: string;
  birthDate: string;
  cpf: string;
  companyName: string;
  phone: string;
  cep: string;
  street: string;
  addressNumber: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

interface CheckoutStepBuyerProps {
  buyerData: BuyerData;
  setBuyerData: React.Dispatch<React.SetStateAction<BuyerData>>;
  onNext: () => void;
}

export function CheckoutStepBuyer({ buyerData, setBuyerData, onNext }: CheckoutStepBuyerProps) {
  const { t } = useTranslation("checkout");
  const { user, profile } = useAuth();
  const { states } = useIBGEStates();
  const [loadingCep, setLoadingCep] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-fill from profile on first load
  useEffect(() => {
    if (profileLoaded || !user) return;

    if (profile) {
      setBuyerData((prev) => ({
        ...prev,
        fullName: prev.fullName || profile.full_name || "",
        email: prev.email || user.email || "",
        cpf: prev.cpf || profile.document_number || "",
        phone: prev.phone || (profile.phone ? formatPhone(profile.phone) : ""),
        birthDate: prev.birthDate || profile.birth_date || "",
        cep: prev.cep || (profile.cep ? formatCEP(profile.cep) : ""),
        street: prev.street || profile.street || "",
        addressNumber: prev.addressNumber || profile.address_number || "",
        complement: prev.complement || profile.complement || "",
        neighborhood: prev.neighborhood || profile.neighborhood || "",
        city: prev.city || profile.city || "",
        state: prev.state || profile.state || "",
      }));
    } else {
      setBuyerData((prev) => ({
        ...prev,
        email: prev.email || user.email || "",
      }));
    }
    setProfileLoaded(true);
  }, [user, profile, profileLoaded, setBuyerData]);

  const handleFillFromProfile = useCallback(() => {
    if (!profile && !user) return;
    setBuyerData((prev) => ({
      ...prev,
      fullName: profile?.full_name || prev.fullName,
      email: user?.email || prev.email,
      cpf: profile?.document_number || prev.cpf,
      phone: profile?.phone ? formatPhone(profile.phone) : prev.phone,
      birthDate: profile?.birth_date || prev.birthDate,
      cep: profile?.cep ? formatCEP(profile.cep) : prev.cep,
      street: profile?.street || prev.street,
      addressNumber: profile?.address_number || prev.addressNumber,
      complement: profile?.complement || prev.complement,
      neighborhood: profile?.neighborhood || prev.neighborhood,
      city: profile?.city || prev.city,
      state: profile?.state || prev.state,
    }));
  }, [profile, user, setBuyerData]);

  // CEP auto-fill
  const handleCepChange = useCallback(async (rawValue: string) => {
    const formatted = formatCEP(rawValue);
    setBuyerData((prev) => ({ ...prev, cep: formatted }));

    const clean = rawValue.replace(/\D/g, "");
    if (clean.length === 8) {
      setLoadingCep(true);
      const address = await fetchAddress(clean);
      if (address) {
        setBuyerData((prev) => ({
          ...prev,
          street: address.street || prev.street,
          neighborhood: address.neighborhood || prev.neighborhood,
          city: address.city || prev.city,
          state: address.state || prev.state,
        }));
      }
      setLoadingCep(false);
    }
  }, [setBuyerData]);

  const handleValidateAndNext = () => {
    const newErrors: Record<string, string> = {};

    if (!buyerData.fullName.trim()) newErrors.fullName = t("checkout.validation.fillFullName");
    if (!buyerData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerData.email)) newErrors.email = t("checkout.validation.invalidEmail");
    if (!buyerData.birthDate) newErrors.birthDate = t("checkout.validation.fillBirthDate");

    const docValidation = validateDocument(buyerData.cpf);
    if (!docValidation.valid || !docValidation.type) {
      newErrors.cpf = docValidation.error || t("checkout.validation.invalidDocument");
    } else if (docValidation.type === "cnpj" && !buyerData.companyName.trim()) {
      newErrors.companyName = t("checkout.validation.fillCompanyName");
    }

    if (!buyerData.phone.trim() || normalizePhoneDigits(buyerData.phone).length < 10) newErrors.phone = t("checkout.validation.invalidPhone");
    if (!buyerData.cep.trim() || buyerData.cep.replace(/\D/g, "").length !== 8) newErrors.cep = t("checkout.validation.invalidCep");
    if (!buyerData.street.trim()) newErrors.street = t("checkout.validation.fillStreet");
    if (!buyerData.addressNumber.trim()) newErrors.addressNumber = t("checkout.validation.fillNumber");
    if (!buyerData.neighborhood.trim()) newErrors.neighborhood = t("checkout.validation.fillNeighborhood");
    if (!buyerData.city.trim()) newErrors.city = t("checkout.validation.fillCity");
    if (!buyerData.state.trim()) newErrors.state = t("checkout.validation.fillState");

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    onNext();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-[22px] font-bold tracking-tight text-foreground">{t("checkout.buyerSection.title")}</h2>
          <p className="text-[13px] text-muted-foreground mt-1">{t("checkout.buyerSection.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={handleFillFromProfile}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] border border-border bg-transparent text-xs font-semibold text-foreground cursor-pointer whitespace-nowrap shrink-0 hover:bg-muted transition-colors"
        >
          <User className="h-3 w-3" />
          {t("checkout.buyerSection.fillFromProfile")}
        </button>
      </div>

      {/* Form card */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        {/* Nome completo */}
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-foreground">
            {t("checkout.buyerSection.fullName")} <span className="text-primary ml-0.5">*</span>
          </Label>
          <Input
            value={buyerData.fullName}
            onChange={(e) => { setBuyerData((p) => ({ ...p, fullName: e.target.value })); setErrors((p) => ({ ...p, fullName: "" })); }}
            placeholder={t("checkout.buyerSection.fullNamePlaceholder")}
            autoComplete="name"
            className={errors.fullName ? "border-destructive focus-visible:ring-destructive" : ""}
          />
          {errors.fullName && <span className="text-[11px] text-destructive">{errors.fullName}</span>}
        </div>

        {/* Email */}
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-foreground">
            {t("checkout.buyerSection.email")} <span className="text-primary ml-0.5">*</span>
          </Label>
          <Input
            type="email"
            value={buyerData.email}
            onChange={(e) => { setBuyerData((p) => ({ ...p, email: e.target.value })); setErrors((p) => ({ ...p, email: "" })); }}
            placeholder={t("checkout.buyerSection.emailPlaceholder")}
            autoComplete="email"
            disabled
            className="bg-muted"
          />
          <span className="text-[11px] text-muted-foreground">{t("checkout.buyerSection.emailNotice")}</span>
        </div>

        {/* Data nascimento */}
        <div className="flex flex-col gap-1 max-w-xs">
          <Label className="text-xs font-semibold text-foreground">
            {t("checkout.buyerSection.birthDate")} <span className="text-primary ml-0.5">*</span>
          </Label>
          <Input
            type="date"
            value={buyerData.birthDate}
            onChange={(e) => { setBuyerData((p) => ({ ...p, birthDate: e.target.value })); setErrors((p) => ({ ...p, birthDate: "" })); }}
            className={errors.birthDate ? "border-destructive focus-visible:ring-destructive" : ""}
          />
          {errors.birthDate && <span className="text-[11px] text-destructive">{errors.birthDate}</span>}
        </div>

        {/* Telefone + CPF */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <Label className="text-xs font-semibold text-foreground">{t("checkout.buyerSection.phone")} <span className="text-primary ml-0.5">*</span></Label>
            <Input
              value={buyerData.phone}
              onChange={(e) => { setBuyerData((p) => ({ ...p, phone: formatPhone(e.target.value) })); setErrors((p) => ({ ...p, phone: "" })); }}
              placeholder={t("checkout.buyerSection.phonePlaceholder")}
              autoComplete="tel"
              maxLength={15}
              className={errors.phone ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {errors.phone && <span className="text-[11px] text-destructive">{errors.phone}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <DocumentInput
              value={buyerData.cpf}
              onChange={(val) => { setBuyerData((prev) => ({ ...prev, cpf: val })); setErrors((p) => ({ ...p, cpf: "" })); }}
              onValidation={() => {}}
              label={t("checkout.buyerSection.document")}
              placeholder={t("checkout.buyerSection.documentPlaceholder")}
              required
            />
            {!errors.cpf && <span className="text-[11px] text-muted-foreground">{t("checkout.buyerSection.documentNotice")}</span>}
            {errors.cpf && <span className="text-[11px] text-destructive">{errors.cpf}</span>}
          </div>
        </div>

        {validateDocument(buyerData.cpf).valid && validateDocument(buyerData.cpf).type === "cnpj" && (
          <div className="flex flex-col gap-1">
            <Label className="text-xs font-semibold text-foreground">
              {t("checkout.buyerSection.companyName")} <span className="text-primary ml-0.5">*</span>
            </Label>
            <Input
              placeholder={t("checkout.buyerSection.companyNamePlaceholder")}
              value={buyerData.companyName}
              onChange={(e) => {
                setBuyerData((prev) => ({ ...prev, companyName: e.target.value.slice(0, 100) }));
                setErrors((p) => ({ ...p, companyName: "" }));
              }}
              maxLength={100}
              className={errors.companyName ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {errors.companyName && <span className="text-[11px] text-destructive">{errors.companyName}</span>}
          </div>
        )}

        {/* Separator and Address Header */}
        <div className="border-t border-border/60 my-4 pt-4 col-span-full">
          <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">{t("checkout.buyerSection.billingAddress")}</h3>
        </div>

        {/* CEP */}
        <div className="flex flex-col gap-1 max-w-xs">
          <Label className="text-xs font-semibold text-foreground">
            {t("checkout.buyerSection.cep")} <span className="text-primary ml-0.5">*</span>
          </Label>
          <div className="relative">
            <Input
              value={buyerData.cep}
              onChange={(e) => { handleCepChange(e.target.value); setErrors((p) => ({ ...p, cep: "" })); }}
              placeholder={t("checkout.buyerSection.cepPlaceholder")}
              maxLength={9}
              className={errors.cep ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {loadingCep && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          {errors.cep && <span className="text-[11px] text-destructive">{errors.cep}</span>}
        </div>

        {/* Rua + Número + Complemento */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_120px] gap-4">
          <div className="flex flex-col gap-1">
            <Label className="text-xs font-semibold text-foreground">
              {t("checkout.buyerSection.street")} <span className="text-primary ml-0.5">*</span>
            </Label>
            <Input
              value={buyerData.street}
              onChange={(e) => { setBuyerData((p) => ({ ...p, street: e.target.value })); setErrors((p) => ({ ...p, street: "" })); }}
              placeholder={t("checkout.buyerSection.streetPlaceholder")}
              className={errors.street ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {errors.street && <span className="text-[11px] text-destructive">{errors.street}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs font-semibold text-foreground">
              {t("checkout.buyerSection.number")} <span className="text-primary ml-0.5">*</span>
            </Label>
            <Input
              value={buyerData.addressNumber}
              onChange={(e) => { setBuyerData((p) => ({ ...p, addressNumber: e.target.value })); setErrors((p) => ({ ...p, addressNumber: "" })); }}
              placeholder={t("checkout.buyerSection.numberPlaceholder")}
              className={errors.addressNumber ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {errors.addressNumber && <span className="text-[11px] text-destructive">{errors.addressNumber}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs font-semibold text-foreground">{t("checkout.buyerSection.complement")}</Label>
            <Input
              value={buyerData.complement}
              onChange={(e) => setBuyerData((p) => ({ ...p, complement: e.target.value }))}
              placeholder={t("checkout.buyerSection.complementPlaceholder")}
            />
          </div>
        </div>

        {/* Bairro + Cidade + Estado */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <Label className="text-xs font-semibold text-foreground">
              {t("checkout.buyerSection.neighborhood")} <span className="text-primary ml-0.5">*</span>
            </Label>
            <Input
              value={buyerData.neighborhood}
              onChange={(e) => { setBuyerData((p) => ({ ...p, neighborhood: e.target.value })); setErrors((p) => ({ ...p, neighborhood: "" })); }}
              placeholder={t("checkout.buyerSection.neighborhoodPlaceholder")}
              className={errors.neighborhood ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {errors.neighborhood && <span className="text-[11px] text-destructive">{errors.neighborhood}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs font-semibold text-foreground">
              {t("checkout.buyerSection.city")} <span className="text-primary ml-0.5">*</span>
            </Label>
            <Input
              value={buyerData.city}
              onChange={(e) => { setBuyerData((p) => ({ ...p, city: e.target.value })); setErrors((p) => ({ ...p, city: "" })); }}
              placeholder={t("checkout.buyerSection.cityPlaceholder")}
              className={errors.city ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {errors.city && <span className="text-[11px] text-destructive">{errors.city}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs font-semibold text-foreground">
              {t("checkout.buyerSection.state")} <span className="text-primary ml-0.5">*</span>
            </Label>
            <Select value={buyerData.state} onValueChange={(v) => { setBuyerData((p) => ({ ...p, state: v })); setErrors((p) => ({ ...p, state: "" })); }}>
              <SelectTrigger className={errors.state ? "border-destructive focus:ring-destructive" : ""}>
                <SelectValue placeholder={t("checkout.buyerSection.selectState")} />
              </SelectTrigger>
              <SelectContent>
                {states.map((s) => (
                  <SelectItem key={s.sigla} value={s.sigla}>{s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.state && <span className="text-[11px] text-destructive">{errors.state}</span>}
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="flex items-center justify-between mt-2">
        <Link
          to="/carrinho"
          className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("checkout.backToCart")}
        </Link>
        <button
          type="button"
          onClick={handleValidateAndNext}
          className="inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-primary text-white border-none text-[15px] font-semibold cursor-pointer shadow-sm shadow-black/15 transition-opacity hover:opacity-90"
        >
          {t("checkout.buyerSection.continue")}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
